import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Input,
  Modal,
  Table,
  Tag,
  Typography
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { SearchOutlined } from "@ant-design/icons";

export interface SplitBidPackage {
  id: string;
  label: string;
  // Current milestone name ("Bid Package Initiated"); already human-readable.
  status: string | null;
  // LOW / MEDIUM / HIGH, server-computed; only HIGH is called out in the table.
  risk: string | null;
  linkedMilestone: string | null;
}

export interface SplitResult {
  title: string;
  number: string;
  movedBpIds: string[];
}

interface SplitDesignPackageModalProps {
  open: boolean;
  onClose: () => void;
  sourceTitle: string;
  sourceNumber: string;
  // Named milestones on the source package — the copy retains them, which the
  // info line states.
  milestoneCount: number;
  bidPackages: SplitBidPackage[];
  // Every design package's title/number, so the suggested name and the silently
  // derived number can't collide (the backend only errors generically).
  existingTitles: string[];
  existingNumbers: string[];
  onConfirm: (result: SplitResult) => void;
  confirmLoading: boolean;
}

// The lowest suffix that leaves BOTH the name and the number free, so the pair
// stays in step ("Core – 3" carries number "1-3"). The number is never shown:
// the design asks only for a name, but the API requires a number on the new
// package, so it is derived here rather than prompted for.
function nextFreeSuffix(
  baseTitle: string,
  baseNumber: string,
  titles: string[],
  numbers: string[]
): number {
  const takenTitles = new Set(titles.map((t) => t.trim().toLowerCase()));
  const takenNumbers = new Set(numbers.map((n) => n.trim().toLowerCase()));
  let n = 2;
  while (
    takenTitles.has(`${baseTitle} – ${n}`.toLowerCase()) ||
    takenNumbers.has(`${baseNumber}-${n}`.toLowerCase())
  ) {
    n += 1;
  }
  return n;
}

export default function SplitDesignPackageModal(
  props: SplitDesignPackageModalProps
) {
  const {
    open,
    onClose,
    sourceTitle,
    sourceNumber,
    milestoneCount,
    bidPackages,
    existingTitles,
    existingNumbers,
    onConfirm,
    confirmLoading
  } = props;

  const [name, setName] = useState(sourceTitle);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  // Once the user edits the name we stop re-suggesting, so a refetch landing
  // mid-session can never overwrite what they typed.
  const nameTouched = useRef(false);

  const suffix = useMemo(
    () =>
      nextFreeSuffix(
        sourceTitle,
        sourceNumber,
        existingTitles,
        existingNumbers
      ),
    [sourceTitle, sourceNumber, existingTitles, existingNumbers]
  );

  // Seed ONCE per open. Keying on open's false→true edge (not on the props) is
  // deliberate: the parent passes fresh arrays every render, so depending on
  // them would re-run this and wipe the user's selection mid-session.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      nameTouched.current = false;
      setQuery("");
      setSelected([]);
      setName(`${sourceTitle} – ${suffix}`);
    }
    wasOpen.current = open;
  }, [open, sourceTitle, suffix]);

  // Keep the suggestion unique against the LIVE list while the modal is open:
  // the list refetches after a split, so a name fixed at open time can collide.
  useEffect(() => {
    if (open && !nameTouched.current) setName(`${sourceTitle} – ${suffix}`);
  }, [open, sourceTitle, suffix]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return bidPackages;
    return bidPackages.filter((bp) => bp.label.toLowerCase().includes(q));
  }, [bidPackages, query]);

  const toggle = (id: string) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  // Select-all spans the FILTERED rows, matching what the user can see.
  const filteredIds = filtered.map((bp) => bp.id);
  const selectedInView = filteredIds.filter((id) => selected.includes(id));
  const allInViewSelected =
    filteredIds.length > 0 && selectedInView.length === filteredIds.length;
  const toggleAll = () =>
    setSelected((prev) =>
      allInViewSelected
        ? prev.filter((id) => !filteredIds.includes(id))
        : Array.from(new Set([...prev, ...filteredIds]))
    );

  const trimmed = name.trim();
  const lower = (s: string) => s.trim().toLowerCase();
  const nameTaken =
    trimmed.length > 0 &&
    existingTitles.some(
      (t) => lower(t) === lower(trimmed) && lower(t) !== lower(sourceTitle)
    );
  const movedCount = selected.length;
  // A split needs a package on each side: one bid package moves, one stays.
  const leavesOriginalEmpty =
    bidPackages.length > 0 && movedCount === bidPackages.length;
  const canSplit =
    Boolean(trimmed) && !nameTaken && movedCount > 0 && !leavesOriginalEmpty;

  const columns: ColumnsType<SplitBidPackage> = [
    {
      title: "Bid Package",
      dataIndex: "label",
      ellipsis: true,
      render: (label: string, record) => (
        <span
          className={`!text-[13px] ${
            selected.includes(record.id) ? "!font-medium" : "!font-normal"
          }`}
        >
          {label}
        </span>
      )
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 160,
      render: (status: string | null) =>
        status ? (
          <Tag className="!border-solid !border-ciq-gray-300 !bg-ciq-gray-50 !text-[10px] !font-medium !text-ciq-gray-900">
            {status}
          </Tag>
        ) : (
          <span className="text-ciq-gray-400">—</span>
        )
    },
    {
      title: "Linked milestone",
      dataIndex: "linkedMilestone",
      width: 170,
      ellipsis: true,
      render: (milestone: string | null) =>
        milestone ? (
          <span className="text-xs text-ciq-primary-800">{milestone}</span>
        ) : (
          <span className="text-ciq-gray-400">—</span>
        )
    },
    {
      title: "Risk",
      dataIndex: "risk",
      width: 110,
      render: (risk: string | null) =>
        (risk ?? "").toUpperCase() === "HIGH" ? (
          <Tag
            variant="filled"
            className="!bg-ciq-red-50 !text-[11px] !font-normal !text-ciq-red-800"
          >
            High risk
          </Tag>
        ) : (
          <span className="text-ciq-gray-400">—</span>
        )
    }
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      destroyOnHidden
      width={760}
      classNames={{
        header:
          "!mb-0 !pb-4 border-0 border-b border-solid border-b-ciq-gray-100",
        footer:
          "!mt-0 !pt-4 border-0 border-t border-solid border-t-ciq-gray-100"
      }}
      title={
        <div className="flex flex-col gap-1">
          <Typography.Text className="!text-[18px] !font-semibold">
            Split design package
          </Typography.Text>
          <Typography.Text className="!text-xs !font-normal !text-ciq-gray-900">
            Creates a copy of {sourceTitle} and moves the selected Bid Packages
            into it.
          </Typography.Text>
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            className="!text-[13px]"
            onClick={onClose}
            disabled={confirmLoading}
            data-testid="split-dp-cancel"
          >
            Cancel
          </Button>
          <Button
            type="primary"
            className="!text-[13px]"
            disabled={!canSplit}
            loading={confirmLoading}
            data-testid="split-dp-confirm"
            onClick={() =>
              onConfirm({
                title: trimmed,
                number: `${sourceNumber}-${suffix}`,
                movedBpIds: selected
              })
            }
          >
            Split into new package
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4 pt-1">
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Typography.Text className="!text-xs !font-medium">
              New design package name
            </Typography.Text>
            <Input
              className="!text-[13px]"
              data-testid="split-dp-name"
              value={name}
              status={nameTaken ? "error" : undefined}
              onChange={(e) => {
                nameTouched.current = true;
                setName(e.target.value);
              }}
            />
            {nameTaken && (
              <Typography.Text
                type="danger"
                className="!text-xs"
                data-testid="split-dp-name-error"
              >
                A design package named “{trimmed}” already exists — pick another
                name.
              </Typography.Text>
            )}
          </div>

          {milestoneCount > 0 && (
            <Alert
              type="warning"
              showIcon
              className="self-start !border-0 !bg-ciq-gray-50 !px-2 !py-1 !text-xs"
              title={`${milestoneCount} Milestone${
                milestoneCount === 1 ? "" : "s"
              } and committed dates are retained`}
            />
          )}
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2 text-xs font-medium">
            <span className="flex-1">Select Bid Packages to move</span>
            <span
              className="text-ciq-primary-800"
              data-testid="split-dp-selected-count"
            >
              {movedCount} of {bidPackages.length} selected
            </span>
          </div>

          <Input
            className="!text-[13px]"
            data-testid="split-dp-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search Bid Packages"
            prefix={<SearchOutlined className="text-ciq-gray-300" />}
          />

          {/* Height is capped on this wrapper rather than via Table's
              scroll={{ y }}: that mode adds a scrollbar-placeholder header
              cell, which shows up as a sixth column the design doesn't have. */}
          <div className="max-h-[320px] overflow-y-auto">
            <Table<SplitBidPackage>
              size="small"
              rowKey="id"
              columns={columns}
              dataSource={filtered}
              pagination={false}
              className="[&_.ant-table-thead>tr>th]:!text-[11px] [&_.ant-table-thead>tr>th]:!font-medium [&_.ant-table-thead>tr>th]:!text-ciq-gray-700"
              rowClassName={(record) =>
                selected.includes(record.id) ? "[&>td]:!bg-ciq-primary-bg" : ""
              }
              onRow={(record) => ({
                "data-testid": `split-bp-${record.id}`,
                className: "cursor-pointer",
                onClick: (event) => {
                  // The selection cell has its own toggle. A click there also
                  // bubbles to the row, which would undo the selection.
                  const target = event.target as HTMLElement | null;
                  if (target?.closest(".ant-table-selection-column")) return;
                  toggle(record.id);
                }
              })}
              rowSelection={{
                selectedRowKeys: selected,
                // The header checkbox drives the filtered rows only, so a
                // search narrows what "select all" means.
                columnTitle: (
                  <Checkbox
                    data-testid="split-dp-select-all"
                    checked={allInViewSelected}
                    indeterminate={
                      selectedInView.length > 0 && !allInViewSelected
                    }
                    disabled={filteredIds.length === 0}
                    onChange={toggleAll}
                  />
                ),
                onSelect: (record, _selected, _rows, event) => {
                  event.stopPropagation();
                  toggle(record.id);
                },
                getCheckboxProps: (record) =>
                  ({ "data-testid": `split-move-${record.id}` }) as object
              }}
            />
          </div>
        </div>

        {leavesOriginalEmpty && (
          <Typography.Text
            type="danger"
            className="!text-xs"
            data-testid="split-dp-empty-error"
          >
            Keep at least one Bid Package on {sourceTitle}.
          </Typography.Text>
        )}
      </div>
    </Modal>
  );
}
